/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import styled from "styled-components";
import { Button, Dropdown, FlexContainer, theme } from "@nordcloud/gnui";

export enum SortOrder {
  ASC = "ASC",
  DESC = "DESC",
}

export type SortEvent<T> = {
  order: SortOrder;
  field: T;
};

export type SelectOption = {
  value: string;
  label: string;
};

type Props<T> = {
  options: SelectOption[];
  value: T;
  direction: SortOrder;
  onSortChange: (event: SortEvent<T>) => void;
};

export function SortSelector<T extends string>({ options, value, direction, onSortChange }: Props<T>) {
  const isAscending = direction === SortOrder.ASC;

  const handleChange = (event: Partial<SortEvent<T>>) => {
    onSortChange({
      order: event.order ?? direction,
      field: event.field ?? value,
    });
  };

  return (
    <FlexContainer>
      <StyledDropdown>
        <Dropdown
          name="Select"
          options={options}
          value={value}
          width="15rem"
          // hide search box
          minNumOfOptionsToShowSearchBox={99}
          onChange={(field) => handleChange({ field: field as T })}
        />
        <Button
          data-testid="sort-button"
          size="md"
          severity="low"
          icon={isAscending ? "sortAscending" : "sortDescending"}
          onClick={() => handleChange({ order: isAscending ? SortOrder.DESC : SortOrder.ASC })}
        />
      </StyledDropdown>
    </FlexContainer>
  );
}

const StyledDropdown = styled.div`
  position: relative;

  & > button {
    position: absolute;
    right: calc(${theme.spacing.spacing01} + 1px);
    top: calc(${theme.spacing.spacing01} + 1px);
  }
`;
